// src/utils/aiGesprekken.js
//
// Gesprekkenlijst van de AI-assistent. De lijst per account staat in Strapi
// (api/ai-gesprekken); de berichten zelf staan in PAP en halen we daar op met het
// gesprek-id.
import axios from 'axios';
import { PAP_ASSISTANT_ID, PAP_CONFIG } from './papConfig';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
const ASSISTANT_NAME = 'Doortje';
const MAX_TITEL = 60;

// Wordt verstuurd als de lijst veranderd is, zodat de gesprekkenlijst op het
// dashboard ververst, ook als de wijziging uit de chatwidget komt.
export const AI_GESPREKKEN_CHANGED = 'ai-gesprekken:changed';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('jwt')}` };
}

export function notifyAiGesprekkenChanged() {
  window.dispatchEvent(new Event(AI_GESPREKKEN_CHANGED));
}

export async function listAiGesprekken() {
  const res = await axios.get(`${STRAPI_URL}/api/ai-gesprekken`, { headers: authHeaders() });
  return res.data?.data ?? [];
}

// De widget meldt het gesprek-id bij elk bericht. Eén keer per minuut per gesprek
// doorgeven is genoeg om de lijst op volgorde van gebruik te houden.
const lastRegistered = new Map();
const REGISTER_INTERVAL_MS = 60_000;

export async function registerAiGesprek(conversationId) {
  if (!conversationId) return null;
  const last = lastRegistered.get(conversationId) || 0;
  if (Date.now() - last < REGISTER_INTERVAL_MS) return null;
  lastRegistered.set(conversationId, Date.now());

  try {
    const res = await axios.post(
      `${STRAPI_URL}/api/ai-gesprekken`,
      { data: { pap_conversation_id: conversationId } },
      { headers: authHeaders() }
    );
    notifyAiGesprekkenChanged();
    return res.data?.data ?? null;
  } catch (error) {
    lastRegistered.delete(conversationId);
    console.error('Fout bij opslaan AI-gesprek:', error);
    return null;
  }
}

export async function renameAiGesprek(documentId, titel) {
  const res = await axios.put(
    `${STRAPI_URL}/api/ai-gesprekken/${documentId}`,
    { data: { titel } },
    { headers: authHeaders() }
  );
  return res.data?.data ?? null;
}

export async function deleteAiGesprek(documentId) {
  await axios.delete(`${STRAPI_URL}/api/ai-gesprekken/${documentId}`, { headers: authHeaders() });
}

// Tekstberichten van een gesprek, of null als PAP het gesprek niet (meer) heeft.
export async function fetchTranscript(conversationId) {
  const url =
    `${PAP_CONFIG.runtimeEngineUrl}/conversations/${encodeURIComponent(conversationId)}` +
    `?assistant_id=${encodeURIComponent(PAP_ASSISTANT_ID)}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Gesprek ophalen mislukt: ${res.status}`);
  const conversation = await res.json();
  return (conversation.messages || [])
    .filter((m) => m.type === 'text' && m.text)
    .map((m) => ({
      sender: m.sender === 'user' ? 'user' : 'assistant',
      text: m.text,
      timestamp: m.timestamp || null,
    }));
}

// Eerste vraag van de gebruiker, ingekort op een woordgrens.
export function titelUitTranscript(messages) {
  const eerste = (messages || []).find((m) => m.sender === 'user');
  if (!eerste) return null;
  const tekst = eerste.text.replace(/\s+/g, ' ').trim();
  if (tekst.length <= MAX_TITEL) return tekst;
  const kort = tekst.slice(0, MAX_TITEL);
  const spatie = kort.lastIndexOf(' ');
  return `${(spatie > 30 ? kort.slice(0, spatie) : kort).trim()}…`;
}

function slug(text) {
  return (
    (text || 'gesprek')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'gesprek'
  );
}

// Returns false als PAP het gesprek niet meer heeft.
export async function downloadGesprekPdf(gesprek, { logoUrl } = {}) {
  let messages = await fetchTranscript(gesprek.pap_conversation_id);
  if (!messages) return false;

  // PAP slaat een beurt pas op als het antwoord klaar is. Download je direct na een
  // antwoord, dan eindigt het opgeslagen gesprek nog met je vraag.
  for (let poging = 0; poging < 3 && messages[messages.length - 1]?.sender === 'user'; poging++) {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    messages = (await fetchTranscript(gesprek.pap_conversation_id)) || messages;
  }

  const { maakGesprekPdf } = await import('./gesprekPdf');
  const titel = gesprek.titel || titelUitTranscript(messages) || `Gesprek met ${ASSISTANT_NAME}`;
  const pdf = await maakGesprekPdf({
    titel,
    gestartOp: new Date(gesprek.createdAt || Date.now()),
    messages,
    assistentNaam: ASSISTANT_NAME,
    logoUrl,
  });

  const dag = new Date().toISOString().slice(0, 10);
  pdf.save(`gesprek-${slug(titel)}-${dag}.pdf`);
  return true;
}
